import {
  AdditionalSaveParticipantToResourceMoment,
  DEFAULT_SHEET_MAPPINGS_V2,
  DestinatioType,
  ExperienceTemplateStatus,
  ExternalServiceType,
  IExperienceTemplateResource,
  IExperienceTemplateResourceExcel,
  IExperienceTemplateResourceGoogleSheet,
  IExperienceTemplateResourceSalesforce,
  InternalStorageInstructionType,
  SpreadsheeMapping,
  StorageContext,
  SubTaskDataDestination,
  SubTaskDataDestinationAdpWorkforceNow,
  SubTaskDataDestinationBambooHr,
  SubTaskDataDestinationExcel,
  SubTaskDataDestinationGoogleSheet,
  SubTaskDataDestinationPersonio,
  SubTaskDataDestinationSalesforce,
  SubTaskDataDestinationSpreadsheet,
  SubTaskDataDestinationSupportingActor,
  SubTaskDataDestinationUserProfile,
  TemplateResourceType
} from '@dadohr/shared/domainTypes/experience';
import { Integration } from '@dadohr/shared/domainTypes/integrations';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  CALCULATED_FIELDS,
  CalculatedFieldDefinition,
  CompositeUserFieldKey,
  StandardUserField
} from '@dadohr/shared/domainTypes/user';
import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { assertNever } from 'assert-never';
import { capitalize, compact, isEqual, keyBy, sortBy } from 'lodash';
import {
  ArrowRight,
  ChevronRight,
  Database,
  Edit,
  ExternalLink,
  PlusCircle,
  X
} from 'lucide-react';
import React, {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import shortid from 'shortid';
import { AlertBox } from '../../../../../../../../components/AlertBox';
import {
  AutocompleteSingle,
  AutocompleteSingleSimpleKeyed,
  AutocompleteSingleWithCreateOption
} from '../../../../../../../../components/Autocomplete';
import { ButtonWithPromise } from '../../../../../../../../components/ButtonWithPromise';
import { DialogTitleWithTypography } from '../../../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../../../../components/InfoBox';
import { LinkExternalWithEllipsis } from '../../../../../../../../components/LinkExternal';
import { TokenDefinition } from '../../../../../../../../components/MarkdownText';
import { TextFieldLoader } from '../../../../../../../../components/TextFieldLoader';
import { TextFieldWithReplacementTokens } from '../../../../../../../../components/TextFieldWithReplacementTokens';
import { useDialogState } from '../../../../../../../../hooks/useDialogState';
import { useSnackbar } from '../../../../../../../../hooks/useSnackbar';
import {
  UnsafeTFunction,
  useTranslation,
  useUnsafeTranslation
} from '../../../../../../../../i18next';
import { useNewUx } from '../../../../../../../../services/newUx';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../../../../services/currentUser';
import { toGoogleSheetsLink } from '../../../../../../../../services/google';
import { useRoutes } from '../../../../../../../../routes';
import { useIntegrationsContext } from '../../../../../../../../services/integrations/context';
import {
  compositeUserFields,
  standardUserFields,
  toSearchTermRegexp,
  useUserFields
} from '../../../../../../../../services/user';
import { useAdpWorkforceNowDataDestinationFields } from '../../../../../../../Org/pages/Integrations/integrations/adp/service';
import { getBambooHrFields } from '../../../../../../../Org/pages/Integrations/integrations/bamboohr/service';
import { usePersonioDestinationFields } from '../../../../../../../Org/pages/Integrations/integrations/personio/service';
import {
  useSalesforceObjects,
  useSalesforceDataDestinationFields
} from '../../../../../../../Org/pages/Integrations/integrations/salesforce/service';
import {
  DadoFieldOption,
  HIDDEN_USER_FIELD_KEYS
} from '../../../../../../../Users/pages/Imports/components/CustomImportMappingForm';
import { AddButton } from '../../../../../RuleBuilder';
import { BlockSettingsSubPage, TaskBuilderContext } from '../../../types';
import { TaskBuilderBlockValidationStateItem } from '../../../validation';
import { EditSalesforceResourceDialog } from './EditSalesforceResourceDialog';

type StorageType = {
  type: SubTaskDataDestination['type'];
  label: string;
};

/** i18n prefix for source options. `sources` carries raw keys (persisted on
 * `storage.source`); the display label is `t(`${SOURCE_TRANSLATION_PREFIX}.${key}`)`.
 * Keeping the prefix here means the persisted value stays free of i18n coupling. */
const SOURCE_TRANSLATION_PREFIX = 'subTask.backgroundCheck.invite.screening.types';

/** Resolve the display label for a source key. When no translation exists the
 * `t()` fallback returns the full dotted key, so we surface only the trailing
 * segment (the raw source) instead of the prefixed i18n key. */
const translateSourceLabel = (
  t: (key: string) => string,
  source: string
): string => {
  const fullKey = `${SOURCE_TRANSLATION_PREFIX}.${source}`;
  const translated = t(fullKey);
  return translated === fullKey ? source.split('.').pop() ?? source : translated;
};

const DEFAULT_COMPLETION_TEXT = 'Completed at {{ completionTime }}';

const StorageTypeSelector = ({
  value,
  onChange,
  disallowUserProfile = false
}: {
  value: SubTaskDataDestination['type'];
  onChange: (nextType: SubTaskDataDestination['type']) => void;
  disallowUserProfile: boolean;
}) => {
  console.log('VALUE', value);
  const { t } = useUnsafeTranslation();
  const { active: activeIntegrations } = useIntegrationsContext();
  console.log('ACTIVE', activeIntegrations);
  const storageTypes = useMemo<StorageType[]>(() => {
    return compact([
      activeIntegrations.has(Integration.ADP_WORKFORCE_NOW) && {
        label: 'ADP Workforce Now',
        type: ExternalServiceType.ADP_WORKFORCE_NOW
      },
      (activeIntegrations.has(Integration.GOOGLE_SHEETS) ||
        value === ExternalServiceType.GOOGLE_SHEET) && {
        label: 'Google Sheet',
        type: ExternalServiceType.GOOGLE_SHEET
      },
      (activeIntegrations.has(Integration.MICROSOFT) ||
        value === ExternalServiceType.MS_EXCEL) && {
        label: 'Microsoft Excel',
        type: ExternalServiceType.MS_EXCEL
      },
      (activeIntegrations.has(Integration.BAMBOOHR) ||
        value === ExternalServiceType.BAMBOO_HR) && {
        label: 'BambooHR',
        type: ExternalServiceType.BAMBOO_HR
      },
      (activeIntegrations.has(Integration.PERSONIO) ||
        value === ExternalServiceType.BAMBOO_HR) && {
        label: 'Personio',
        type: ExternalServiceType.PERSONIO
      },
      (activeIntegrations.has(Integration.SALESFORCE) ||
        value === ExternalServiceType.SALESFORCE) && {
        label: 'Salesforce',
        type: ExternalServiceType.SALESFORCE
      },
      !disallowUserProfile && {
        label: t('Dado'),
        type: InternalStorageInstructionType.USER_PROFILE
      }
    ]);
  }, [activeIntegrations, value, t, disallowUserProfile]);

  const th = useTheme();
  const id = 'subTask-question-body-edit-dialog-storage-selector';
  return (
    <FormControl fullWidth>
      <InputLabel
        id={id}
        variant="outlined"
        style={{
          background: th.palette.background.paper,
          padding: `0 ${th.spacing(0.5)}px`
        }}
      >
        {t('subTask.question.dialog.storageSelector.label')}
      </InputLabel>
      <Select
        labelId={id}
        variant="outlined"
        fullWidth
        value={value}
        onChange={(ev) =>
          onChange(ev.target.value as SubTaskDataDestination['type'])
        }
      >
        {storageTypes.map((t) => (
          <MenuItem key={t.type} value={t.type}>
            {t.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const getGoogleSheetResource = (
  res: IExperienceTemplateResource[],
  resourceId: string
): IExperienceTemplateResourceGoogleSheet | null => {
  for (const r of res) {
    if (r.type === TemplateResourceType.GOOGLE_SHEET) {
      if (resourceId === r.resourceId) {
        return r;
      }
    }
  }
  return null;
};

const getExcelResource = (
  res: IExperienceTemplateResource[],
  resourceId: string
): IExperienceTemplateResourceExcel | null => {
  for (const r of res) {
    if (r.type === TemplateResourceType.MS_EXCEL) {
      if (resourceId === r.resourceId) {
        return r;
      }
    }
  }
  return null;
};

const getSalesforceResource = (
  res: IExperienceTemplateResource[],
  resourceId: string
): IExperienceTemplateResourceSalesforce | null => {
  for (const r of res) {
    if (r.type === TemplateResourceType.SALESFORCE) {
      if (resourceId === r.resourceId) {
        return r;
      }
    }
  }
  return null;
};

const getOptionalResource = (
  res: IExperienceTemplateResource[],
  storage: SubTaskDataDestination
) => {
  switch (storage.type) {
    case ExternalServiceType.GOOGLE_SHEET:
      return getGoogleSheetResource(res, storage.resourceId);
    case ExternalServiceType.MS_EXCEL:
      return getExcelResource(res, storage.resourceId);
    case ExternalServiceType.ADP_WORKFORCE_NOW:
      return null;
    case ExternalServiceType.BAMBOO_HR:
      return null;
    case ExternalServiceType.PERSONIO:
      return null;
    case ExternalServiceType.SALESFORCE:
      return getSalesforceResource(res, storage.resourceId);
    case InternalStorageInstructionType.USER_PROFILE:
      return null;
    case InternalStorageInstructionType.SUPPORTING_ACTOR:
      return null;

    default:
      return assertNever(storage);
  }
};

const CreateNewFieldLink = () => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const { ROUTES } = useRoutes();
  const baseUrl = ROUTES.org.customFields.url();
  const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}action=add`;
  return (
    <ButtonBase
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={css`
        && {
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1)}px ${theme.spacing(2)}px;
          color: ${theme.palette.primary.main};
          font-size: ${theme.typography.body2.fontSize};
          gap: ${theme.spacing(1)}px;
          border-top: 1px solid ${theme.palette.divider};
        }
        &:hover {
          background-color: ${theme.palette.action.hover};
        }
      `}
    >
      <PlusCircle size={16} />
      {t('Create new field')}
    </ButtonBase>
  );
};

const UserProfileSettings = ({
  value,
  onChange,
  expTpl,
  customFields: _customFieldsProp
}: {
  value: SubTaskDataDestinationUserProfile;
  onChange: (nextValue: SubTaskDataDestinationUserProfile) => void;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
}) => {
  const { t } = useTranslation();
  const { org } = useCurrentUser();
  const customFields = org.customUserFields;
  const userFields = useUserFields(customFields);
  const selectedField = useMemo(() => {
    return userFields.find((f) => f.key === value.field) || null;
  }, [userFields, value]);
  const popperFooter = useMemo(() => <CreateNewFieldLink />, []);
  return (
    <FormRow>
      <AutocompleteSingle
        label={`${t('subTask.question.storage.userProfile.field')}`}
        value={selectedField}
        disabledClearable
        getOptionLabel={(o) => o.label}
        getOptionSelected={(o, v) => o.key === v.key}
        options={userFields}
        onChange={(field) => {
          onChange({ ...value, field: field?.key || '' });
        }}
        fullWidth
        popperFooter={popperFooter}
      />
    </FormRow>
  );
};

const AdpWorkforceNowSettings = ({
  value,
  onChange,
  expTpl
}: {
  value: SubTaskDataDestinationAdpWorkforceNow;
  onChange: (nextStorage: SubTaskDataDestinationAdpWorkforceNow) => void;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  const [options] = useAdpWorkforceNowDataDestinationFields(expTpl.orgId);
  if (!options) {
    return (
      <FormRow>
        <TextFieldLoader
          label={t('Field')}
          placeholder={t('Loading ADP data...')}
          fullWidth
        />
      </FormRow>
    );
  }
  const selectedField = options.find((o) => o.key === value.field) || null;
  const showCodeHint = selectedField?.type === 'code';
  return (
    <>
      <FormRow>
        <AutocompleteSingle
          label={`${t('Field')}`}
          value={selectedField}
          options={options}
          onChange={(field) => {
            field && onChange({ ...value, field: field.key });
          }}
          getOptionLabel={(o) => o.label}
          renderOption={(o) => o.label}
          getOptionSelected={(a, b) => a.key === b.key}
          filterOptions={(os, s) => {
            const re = toSearchTermRegexp(s.inputValue);
            return os.filter((o) => o.label.match(re));
          }}
          fullWidth
        />
      </FormRow>
      <FormRow>
        {showCodeHint ? (
          <InfoBox color="yellow">
            {t(
              '{{ hris }} will only accept certain values in this field. Please check in {{ hris }} to see what will be accepted.',
              {
                hris: 'ADP Workforce Now'
              }
            )}
          </InfoBox>
        ) : (
          <InfoBox color="blue">
            Can't find the field you're looking for? Contact{' '}
            <a href="mailto:help@dadohr.com">help@dadohr.com</a> to request an
            update to your integration.
          </InfoBox>
        )}
      </FormRow>
    </>
  );
};

const PersonioSettings = ({
  value,
  onChange,
  expTpl
}: {
  value: SubTaskDataDestinationPersonio;
  onChange: (nextStorage: SubTaskDataDestinationPersonio) => void;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  const [options] = usePersonioDestinationFields(expTpl.orgId);
  if (!options) {
    return (
      <FormRow>
        <TextFieldLoader
          label={t('Field')}
          placeholder={t('Loading Personio data...')}
          fullWidth
        />
      </FormRow>
    );
  }
  const selectedField = options.find((o) => o.key === value.field) || null;

  return (
    <>
      <FormRow>
        <AutocompleteSingle
          label={`${t('Field')}`}
          value={selectedField}
          options={options}
          onChange={(field) => {
            field && onChange({ ...value, field: field.key });
          }}
          getOptionLabel={(o) => o.label}
          renderOption={(o) => o.label}
          getOptionSelected={(a, b) => a.key === b.key}
          filterOptions={(os, s) => {
            const re = toSearchTermRegexp(s.inputValue);
            return os.filter((o) => o.label.match(re));
          }}
          fullWidth
        />
      </FormRow>
      <FormRow>
        <InfoBox color="yellow">
          Can't find the field you're looking for? Make sure the Personio API
          credentials grant Dado access to this field. Please note that Personio
          does not permit all fields to be edited by external systems like Dado,
          so these fields are excluded from this list.
        </InfoBox>
      </FormRow>
    </>
  );
};

/**
 * Salesforce subtask-destination settings.
 *
 * Two-level config (parity with Google Sheets resources):
 *   - Pick a template-level IExperienceTemplateResourceSalesforce. Each
 *     resource defines a primary record to upsert at instance launch
 *     (objectName + externalIdField + fieldMap). Multiple may coexist on a
 *     template (e.g. a provider Contact + a clinic Account). The selected
 *     resource governs WHICH parent record this subtask's write attaches to.
 *   - Pick the subtask's own Object + Field — typically the same object as
 *     the resource's primary (writes to that record directly) or a child
 *     object (e.g. License__c parented to the resolved Contact via Contact__c).
 *
 * Per BYOCA, every customer brings their own Connected App and SF schema, so
 * the object/field pickers are dynamic: describeObjects() lists writable
 * SObjects and per-object describes yield field-level metadata for the field
 * dropdown + length validation.
 */
const SalesforceSettings = ({
  value,
  onChange,
  resources,
  onChangeResources,
  expTpl
}: {
  value: SubTaskDataDestinationSalesforce;
  onChange: (nextStorage: SubTaskDataDestinationSalesforce) => void;
  resources: IExperienceTemplateResource[];
  onChangeResources: (next: IExperienceTemplateResource[]) => void;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  const [objectsResp, objectsLoading] = useSalesforceObjects(expTpl.orgId);
  const [fieldsResp, fieldsLoading] = useSalesforceDataDestinationFields(
    expTpl.orgId,
    value.objectName ?? ''
  );

  const sfResources = useMemo(
    () =>
      resources.filter(
        (r): r is IExperienceTemplateResourceSalesforce =>
          r.type === TemplateResourceType.SALESFORCE
      ),
    [resources]
  );

  const selectedResource = useMemo(
    () => sfResources.find((r) => r.resourceId === value.resourceId) || null,
    [sfResources, value.resourceId]
  );

  // The resource being edited in the dialog. Distinct from selectedResource —
  // can be a brand-new resource the admin is configuring before linking it.
  const [editedResource, setEditedResource] = useState<
    IExperienceTemplateResourceSalesforce | null
  >(null);

  const upsertResource = (next: IExperienceTemplateResourceSalesforce) => {
    const exists = resources.some(
      (r) =>
        r.type === TemplateResourceType.SALESFORCE && r.resourceId === next.resourceId
    );
    onChangeResources(
      exists
        ? resources.map((r) =>
            r.type === TemplateResourceType.SALESFORCE && r.resourceId === next.resourceId
              ? next
              : r
          )
        : [...resources, next]
    );
    if (value.resourceId !== next.resourceId) {
      onChange({ ...value, resourceId: next.resourceId });
    }
  };

  const objectOptions = useMemo(
    () =>
      sortBy(
        objectsResp?.objects.filter((o) => o.createable || o.updateable) ?? [],
        (o) => o.label
      ),
    [objectsResp]
  );

  const fieldOptions = useMemo(() => fieldsResp?.fields ?? [], [fieldsResp]);
  const selectedField =
    value.fieldName != null
      ? fieldOptions.find((f) => f.name === value.fieldName) || null
      : null;

  if (objectsLoading && !objectsResp) {
    return (
      <FormRow>
        <TextFieldLoader
          label={t('Salesforce data destination')}
          placeholder={t('Loading Salesforce schema...')}
          fullWidth
        />
      </FormRow>
    );
  }

  return (
    <>
      <FormRow>
        <AutocompleteSingleWithCreateOption
          fullWidth
          label={`${t('Salesforce data destination')}`}
          value={selectedResource}
          onChange={(r) => onChange({ ...value, resourceId: r.resourceId })}
          options={sfResources}
          getOptionLabel={(o) =>
            o.objectName
              ? `${o.objectName} (matched by ${o.externalIdField || '—'})`
              : t('Untitled Salesforce destination')
          }
          renderOption={(o) => (
            <FlexContainer alignItems="center" spacing={0.5}>
              <FlexContainerVertical>
                <span>
                  {o.objectName || t('Untitled')}{' '}
                  <small style={{ opacity: 0.6 }}>
                    {o.externalIdField
                      ? `matched by ${o.externalIdField}`
                      : t('unconfigured')}
                  </small>
                </span>
                <FlexContainer spacing={2}>
                  <Button
                    size="small"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      setEditedResource(o);
                    }}
                    startIcon={<Edit size={12} />}
                  >
                    {t('Edit Field Mappings')}
                  </Button>
                </FlexContainer>
              </FlexContainerVertical>
            </FlexContainer>
          )}
          alwaysShowCreateOption
          renderCreateOption={(inputValue) =>
            inputValue
              ? `${t('Create')} "${inputValue}"`
              : t('Add a Salesforce destination')
          }
          renderCreateDialog={({ inputValue, close }) => {
            const fresh: IExperienceTemplateResourceSalesforce = {
              type: TemplateResourceType.SALESFORCE,
              resourceId: shortid(),
              status: 'PENDING',
              objectName: inputValue || 'Contact',
              externalIdField: '',
              fieldMap: {}
            };
            return (
              <EditSalesforceResourceDialog
                open={true}
                orgId={expTpl.orgId}
                value={fresh}
                onChange={(next) => upsertResource(next)}
                onClose={close}
              />
            );
          }}
        />
      </FormRow>
      {!selectedResource && (
        <FormRow>
          <InfoBox color="yellow">
            {t(
              'Pick a Salesforce destination (or create one) to choose the parent record this subtask writes through.'
            )}
          </InfoBox>
        </FormRow>
      )}
      {!!selectedResource && (
        <>
          <FormRow>
            <AutocompleteSingleSimpleKeyed
              label={`${t('Salesforce object')}`}
              value={value.objectName || ''}
              options={objectOptions.map((o) => ({
                key: o.name,
                label: `${o.label} (${o.name})`
              }))}
              toSelectKey={(o) => o.key}
              toLabel={(o) => o.label}
              onChange={(key) => {
                // Reset fieldName whenever the object changes — prevents stale
                // field selections from a different object.
                onChange({ ...value, objectName: key, fieldName: null });
              }}
              fullWidth
            />
          </FormRow>
          {!!value.objectName && (
            <FormRow>
              {fieldsLoading && !fieldsResp ? (
                <TextFieldLoader
                  label={t('Salesforce field')}
                  placeholder={t('Loading fields...')}
                  fullWidth
                />
              ) : (
                <AutocompleteSingle
                  label={`${t('Salesforce field')}`}
                  value={selectedField}
                  options={fieldOptions}
                  onChange={(field) => {
                    onChange({ ...value, fieldName: field ? field.name : null });
                  }}
                  getOptionLabel={(o) => `${o.label} (${o.name})`}
                  renderOption={(o) => (
                    <FlexContainerVertical>
                      <span>
                        {o.label}{' '}
                        <small style={{ opacity: 0.6 }}>({o.name})</small>
                      </span>
                      <small style={{ opacity: 0.6 }}>
                        {o.type}
                        {o.length ? ` · max ${o.length} chars` : ''}
                      </small>
                    </FlexContainerVertical>
                  )}
                  getOptionSelected={(a, b) => a.name === b.name}
                  filterOptions={(os, s) => {
                    const re = toSearchTermRegexp(s.inputValue);
                    return os.filter(
                      (o) => o.label.match(re) || o.name.match(re)
                    );
                  }}
                  fullWidth
                />
              )}
            </FormRow>
          )}
          {selectedField?.length != null && selectedField.length > 0 && (
            <FormRow>
              <InfoBox color="yellow">
                {t(
                  `Salesforce limits ${selectedField.label} to ${selectedField.length} characters. Longer answers will be truncated at write time and a warning logged.`
                )}
              </InfoBox>
            </FormRow>
          )}
          <FormRow>
            <Tooltip
              title={t(
                "Field not in the list? Make sure your Salesforce Connected App's profile permission set has Edit access to the field."
              )}
            >
              <InfoBox color="yellow">
                {t(
                  "Can't find the field? The Salesforce Connected App's profile must have Edit access to it. System-managed fields (Id, audit timestamps) are filtered out."
                )}
              </InfoBox>
            </Tooltip>
          </FormRow>
        </>
      )}
      {!!editedResource && (
        <EditSalesforceResourceDialog
          open={true}
          orgId={expTpl.orgId}
          value={editedResource}
          onChange={(next) => upsertResource(next)}
          onClose={() => setEditedResource(null)}
        />
      )}
    </>
  );
};

const BambooHrSettings = ({
  value,
  onChange,
  expTpl
}: {
  value: SubTaskDataDestinationBambooHr;
  onChange: (nextStorage: SubTaskDataDestinationBambooHr) => void;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useTranslation();
  return (
    <FormRow>
      <AutocompleteSingle
        label={`${t('subTask.question.storage.bambooHr.field')}`}
        value={value.field}
        getOptionLabel={(o) => o.name}
        getOptionSelected={(o, v) => o.id === v.id}
        options={() => getBambooHrFields(expTpl.orgId)}
        onChange={(field) => {
          onChange({ ...value, field });
        }}
        fullWidth
      />
    </FormRow>
  );
};

const toCustomFieldsKey = (k: string) => `customFields.${k}`;

export const customFieldToSheetMapping = (
  def: CustomUserField
): SpreadsheeMapping => {
  const dadoKey = toCustomFieldsKey(def.key);
  return {
    dadoKey,
    column: def.label
  };
};

type SheetMappingsFormData = {
  def: StandardUserField | CustomUserField;
  column: string;
};

const SheetMappingsGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  gridColumnGap: p.theme.spacing(2),
  gridRowGap: p.theme.spacing(1),
  alignItems: 'center',
  width: '100%'
}));

const createSheetMappingsFormData = (
  mappings: SpreadsheeMapping[],
  customUserFields: CustomUserField[],
  t: UnsafeTFunction
): SheetMappingsFormData[] => {
  const mappingsByDadoKey = keyBy(mappings, (m) => m.dadoKey);
  return [
    ...standardUserFields(t).map((def) => {
      const m = mappingsByDadoKey[def.key];
      return {
        def,
        column: m ? m.column : ''
      };
    }),
    ...customUserFields.map((def) => {
      const key = toCustomFieldsKey(def.key);
      const m = mappingsByDadoKey[key];
      return {
        def: { ...def, key },
        column: m ? m.column : ''
      };
    })
  ];
};

export const SheetMappingForm = ({
  value,
  onChange,
  customUserFields
}: {
  value: SpreadsheeMapping[];
  onChange: (nextValue: SpreadsheeMapping[]) => void;
  customUserFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();
  const data = useMemo(() => {
    return createSheetMappingsFormData(value, customUserFields, t);
  }, [value, customUserFields, t]);

  return (
    <SheetMappingsGrid>
      <strong>{t('import.mapping.dado')}</strong>
      <strong>{t('Column')}</strong>
      {data.map((d) => (
        <Fragment key={d.def.key}>
          <div>{d.def.label}</div>
          <TextField
            fullWidth
            value={d.column}
            onChange={(ev) => {
              const i = value.findIndex((v) => v.dadoKey === d.def.key);
              const newColumnValue = ev.target.value;

              const columnValueIsEmpty =
                !newColumnValue || !newColumnValue.trim();

              //prevent adding emty mappings
              if (columnValueIsEmpty) {
                if (i >= 0) {
                  const nextValue = value.filter(
                    (v) => v.dadoKey !== d.def.key
                  );
                  onChange(nextValue);
                }
              } else if (i === -1) {
                onChange([
                  ...value,
                  { dadoKey: d.def.key, column: newColumnValue }
                ]);
              } else {
                const nextValue = [...value];
                nextValue[i] = { ...value[i], column: newColumnValue };
                onChange(nextValue);
              }
            }}
          />
        </Fragment>
      ))}
    </SheetMappingsGrid>
  );
};
const FieldMappingContainer = styled('div')`
  margin-block-end: ${(p) => p.theme.spacing(2)}px;
  width: 100%;
`;

const Grid2 = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns:
    'minmax(min-content, 400px) min-content minmax(min-content, 400px) min-content',
  gridColumnGap: p.theme.spacing(1),
  gridRowGap: p.theme.spacing(1),
  alignItems: 'center'
}));

const Grid2Row = styled('div')`
  grid-column: span 4;
  margin-block-start: ${(p) => p.theme.spacing(-1)}px;
`;

const isMappingComplete = (mapping: SpreadsheeMapping): boolean =>
  !!(mapping.dadoKey && mapping.column);

const CustomImportMappingFormRow = ({
  userFields,
  value,

  onBlur,
  onChange,
  onRemove
}: {
  userFields: DadoFieldOption[];
  value: SpreadsheeMapping;
  onBlur: () => any;
  onChange: (mapping: SpreadsheeMapping) => any;
  onRemove: () => any;
}) => {
  const { closeDialog } = useDialogState();
  const { t } = useUnsafeTranslation();
  const theme = useTheme();

  const [isDadoDirty, setIsDadoDirty] = useState(false);
  const [isOtherDirty, setIsOtherDirty] = useState(false);

  const { enqueueSnackbarError } = useSnackbar();

  const removeHandler = () => {
    closeDialog();
    onRemove();
  };

  return (
    <>
      <div>
        <AutocompleteSingleSimpleKeyed
          value={value.dadoKey}
          options={sortBy(userFields, (x) => x.label)}
          toSelectKey={(o) => o.key}
          toLabel={(o) => o.label}
          fullWidth
          onBlur={() => {
            setIsDadoDirty(true);

            if (isDadoDirty && isOtherDirty) {
              onBlur();
            }
          }}
          onChange={(nextDadoKey) => {
            setIsDadoDirty(true);
            onChange({ ...value, dadoKey: nextDadoKey });
          }}
        />
      </div>
      <div>
        <ArrowRight size="14" color={theme.custom.colors.gray.w500} />
      </div>

      <div>
        <TextField
          value={value.column}
          fullWidth
          onBlur={() => {
            setIsOtherDirty(true);

            if (isDadoDirty && isOtherDirty) {
              onBlur();
            }
          }}
          onChange={(e) => {
            setIsOtherDirty(true);
            onChange({ ...value, column: e.target.value });
          }}
          aria-labelledby="heading-field-source"
        />
      </div>
      <div>
        <IconButton aria-label={t('Delete mapping')} onClick={removeHandler}>
          <X size="14" />
        </IconButton>
      </div>

      {(isDadoDirty || value.dadoKey) &&
        isOtherDirty &&
        !isMappingComplete(value) && (
          <Grid2Row>
            <AlertBox color="red">
              {t('Please supply both a Dado field and a target column.')}
            </AlertBox>
          </Grid2Row>
        )}
    </>
  );
};

const createEmptySpreadsheeMapping = (): SpreadsheeMapping => ({
  dadoKey: '',
  column: ''
});

export const SpreadsheetMappingForm = ({
  additionalCompositeFields = [],
  fieldTerm,
  integrationName,
  mappings,
  userFields,
  customFields,
  displayNames,
  onChange
}: {
  additionalCompositeFields?: CompositeUserFieldKey[];
  customFields: CustomUserField[];
  fieldTerm?: string;
  integrationName: string;
  mappings: SpreadsheeMapping[];
  userFields: (StandardUserField | CalculatedFieldDefinition)[];
  displayNames?: Record<string, string>;
  onChange: (mapping: SpreadsheeMapping[]) => any;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const fieldTermOrDefault = fieldTerm ?? t('field');

  const [isDirty, setIsDirty] = useState(false);
  const version = useRef(0);

  const userFieldsWithComposites: DadoFieldOption[] = useMemo(
    () =>
      [
        ...compositeUserFields(additionalCompositeFields, t),
        ...customFields.map((c) => ({
          label: c.label,
          key: toCustomFieldsKey(c.key)
        })),
        ...userFields
      ].filter((f) => !HIDDEN_USER_FIELD_KEYS.includes(f.key as any)),
    [userFields, customFields, additionalCompositeFields, t]
  );

  return (
    <FieldMappingContainer>
      <FlexContainer justifyContent="space-between">
        <Typography
          component="h3"
          variant="h5"
          style={{ marginBlockEnd: theme.spacing(2) }}
        >
          {t('Data fields')}
        </Typography>
      </FlexContainer>

      <Typography
        style={{ marginBottom: theme.spacing(2) }}
        variant="caption"
        component="p"
      >
        {t(
          'Select which Dado data fields should sync to which {{ fieldTerm }} in {{ integration }}',
          {
            fieldTerm: fieldTermOrDefault,
            integration: integrationName
          }
        )}
      </Typography>

      <Grid2>
        <div>
          <Typography variant="caption">{t('import.mapping.dado')}</Typography>
        </div>

        <div>&nbsp;</div>

        <div>
          <Typography variant="caption" id="heading-field-source">
            {t('import.mapping.integration', {
              fieldTerm: capitalize(fieldTermOrDefault),
              integration: integrationName
            })}
          </Typography>
        </div>

        <div>&nbsp;</div>

        {mappings.map((mapping, i) => (
          <>
            <CustomImportMappingFormRow
              key={`${version.current}-${i}`}
              userFields={userFieldsWithComposites}
              value={mapping}
              onBlur={() => setIsDirty(true)}
              onChange={(mappingNew) => {
                onChange(mappings.map((mo, j) => (i === j ? mappingNew : mo)));
              }}
              onRemove={() => {
                if (mappings.length > 1) {
                  onChange(mappings.filter((mo, j) => i !== j));
                } else {
                  // Increment version generates new keys for this component, resetting their internal state
                  version.current++;
                  onChange([createEmptySpreadsheeMapping()]);
                }
              }}
            />
          </>
        ))}

        <div>
          <AddButton
            label={t('Add field')}
            onClick={() => {
              setIsDirty(true);
              onChange([...mappings, createEmptySpreadsheeMapping()]);
            }}
          />
        </div>
      </Grid2>
    </FieldMappingContainer>
  );
};

export const EditSpreadSheetDialog = ({
  open,
  onClose,
  value,
  onChange,
  onChangeAsync,
  onRequestAccess,
  customFields,
  mode = 'edit'
}: {
  open: boolean;
  onClose: () => void;
  value:
    | IExperienceTemplateResourceGoogleSheet
    | IExperienceTemplateResourceExcel;
  onChange?: (
    nextResource:
      | IExperienceTemplateResourceGoogleSheet
      | IExperienceTemplateResourceExcel
  ) => void;
  onChangeAsync?: (
    nextResource:
      | IExperienceTemplateResourceGoogleSheet
      | IExperienceTemplateResourceExcel
  ) => Promise<any>;
  customFields: CustomUserField[];
  mode?: 'create' | 'edit' | 'update';
  onRequestAccess?: () => Promise<any>;
}) => {
  const { t } = useUnsafeTranslation();
  const [v, setV] = useState({
    additionalSaveParticipantToResourceMoments:
      value.additionalSaveParticipantToResourceMoments,
    docTitle: value.docTitle,
    mappings: value.mappings
  });

  const hasChanges = useMemo(() => {
    return !isEqual(
      [v.additionalSaveParticipantToResourceMoments, v.docTitle, v.mappings],
      [
        value.additionalSaveParticipantToResourceMoments,
        value.docTitle,
        value.mappings
      ]
    );
  }, [value, v]);

  const mappingsAreComplete = useMemo(() => {
    const result = v.mappings.reduce((acc, mapping) => {
      return acc && isMappingComplete(mapping);
    }, true);
    console.log(result);
    return result;
  }, [v]);
  const status =
    value.type === TemplateResourceType.MS_EXCEL
      ? value.status
      : value.docStatus;

  const url =
    value.type === TemplateResourceType.GOOGLE_SHEET
      ? toGoogleSheetsLink(value.docId, value.sheetId)
      : value.webUrl;
  const isValidTitle = (title: string, type: TemplateResourceType) => {
    return (
      type !== TemplateResourceType.MS_EXCEL ||
      title.match(/[\\?*[\]/]+/) === null
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitleWithTypography>
        {mode === 'create'
          ? t('Create Data Destination')
          : t('Edit Data Destination')}
      </DialogTitleWithTypography>
      <DialogContent>
        <FlexContainerVertical spacing={4}>
          <TextField
            variant="outlined"
            label={`${t('subTask.question.storage.googleSheets.docTitle')}`}
            helperText={
              isValidTitle(v.docTitle, value.type)
                ? `${t(
                    'subTask.question.storage.googleSheets.docTitleEmptyHint'
                  )}`
                : `${t(
                    `You can't use the following characters in your Data Destination name`
                  )}: \\ / ? *`
            }
            value={v.docTitle}
            disabled={status !== 'PENDING'}
            error={!isValidTitle(v.docTitle, value.type)}
            onChange={(ev) =>
              setV((x) => ({ ...x, docTitle: ev.target.value }))
            }
            fullWidth
          />
          {status === 'COMPLETE' && (
            <FlexContainer fullWidth>
              <LinkExternalWithEllipsis color="primary" href={url}>
                {url}
              </LinkExternalWithEllipsis>
              {onRequestAccess &&
                (value.type === TemplateResourceType.GOOGLE_SHEET ||
                  value.type === TemplateResourceType.MS_EXCEL) && (
                  <ButtonWithPromise
                    variant="outlined"
                    pending={t('Granting access...')}
                    onClick={async () => {
                      await onRequestAccess();
                    }}
                  >
                    {t('Request access')}
                  </ButtonWithPromise>
                )}
            </FlexContainer>
          )}
          <div>
            <FormControlLabel
              control={
                <Switch
                  checked={v.additionalSaveParticipantToResourceMoments.includes(
                    AdditionalSaveParticipantToResourceMoment.ENROLLMENT
                  )}
                  onChange={(ev) =>
                    setV((x) => ({
                      ...x,
                      additionalSaveParticipantToResourceMoments: [
                        ...x.additionalSaveParticipantToResourceMoments.filter(
                          (m) =>
                            m !==
                            AdditionalSaveParticipantToResourceMoment.ENROLLMENT
                        ),
                        ...(ev.target.checked
                          ? [
                              AdditionalSaveParticipantToResourceMoment.ENROLLMENT
                            ]
                          : [])
                      ]
                    }))
                  }
                />
              }
              label={t(
                'Add participants to this spreadsheet when they start the experience'
              )}
            />
            <Typography component="p" variant="caption">
              {t(
                ' By default, participants are added to this spreadsheet when they complete the first subtask which uses this spreadsheet as data destination. Use this toggle to override this setting.'
              )}
            </Typography>
          </div>
          <div>
            <SpreadsheetMappingForm
              integrationName={
                value.type === TemplateResourceType.GOOGLE_SHEET
                  ? 'GoogleSheets'
                  : 'Excel'
              }
              mappings={v.mappings}
              onChange={(nextMappings) =>
                setV((x) => ({ ...x, mappings: nextMappings }))
              }
              fieldTerm="column"
              customFields={customFields}
              userFields={[
                ...standardUserFields(t),
                ...compact(
                  CALCULATED_FIELDS.map((c) => {
                    if (c.type === 'recurringDate') {
                      const result: CalculatedFieldDefinition = {
                        ...c,
                        key: `calculatedFields.recurringDate.${c.field}.current.date`
                      };
                      return result;
                    }
                    return null;
                  })
                )
              ]}
            />
          </div>

          <Typography component="h3" variant="h5">
            {t('Sub-tasks')}
          </Typography>

          <Typography variant="caption" component="p">
            {t(
              'To sync data from sub-tasks to this sheet, open the task and add this sheet as a data destination for the relevant sub-task.'
            )}
          </Typography>
        </FlexContainerVertical>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('Cancel')}
        </Button>
        {onChange && (
          <Button
            onClick={() => {
              onChange({
                ...value,
                ...v
              });
              onClose();
            }}
            variant="contained"
            color="primary"
            disabled={
              !isValidTitle(v.docTitle, value.type) ||
              (mode !== 'create' && !(hasChanges && mappingsAreComplete))
            }
          >
            {mode === 'create'
              ? t('Create')
              : mode === 'update'
              ? t('Save')
              : t('Edit')}
          </Button>
        )}
        {onChangeAsync && (
          <ButtonWithPromise
            pending={t('Saving...')}
            onClick={async () => {
              await onChangeAsync({
                ...value,
                ...v
              });
              onClose();
            }}
            variant="contained"
            color="primary"
            disabled={
              !isValidTitle(v.docTitle, value.type) ||
              (mode !== 'create' && !(hasChanges && mappingsAreComplete))
            }
          >
            {mode === 'create'
              ? t('Create!!!')
              : mode === 'update'
              ? t('Save')
              : t('Edit')}
          </ButtonWithPromise>
        )}
      </DialogActions>
    </Dialog>
  );
};

const GoogleSheetAdorment = ({
  status,
  tplStatus
}: {
  status: IExperienceTemplateResourceGoogleSheet['docStatus'];
  tplStatus: ExperienceTemplateStatus;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <InputAdornment position="start">
      <Tooltip
        classes={{ tooltip: css({ '&&': { maxWidth: 'none' } }) }}
        title={
          status === 'PENDING'
            ? `${t(
                tplStatus === ExperienceTemplateStatus.DRAFT ||
                tplStatus === ExperienceTemplateStatus.ROUGH_DRAFT
                  ? 'This spreadsheet will be created once you launch your experience'
                  : `This spreadsheet will be created once you save your changes`
              )}`
            : ''
        }
      >
        <img
          alt=""
          height={18}
          src={
            status === 'PENDING'
              ? '/images/resources/gsheet_disabled.svg'
              : '/images/resources/gsheet.svg'
          }
        />
      </Tooltip>
    </InputAdornment>
  );
};

const ExcelSheetAdorment = ({
  status,
  tplStatus
}: {
  status: IExperienceTemplateResourceExcel['status'];
  tplStatus: ExperienceTemplateStatus;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <InputAdornment position="start">
      <Tooltip
        classes={{ tooltip: css({ '&&': { maxWidth: 'none' } }) }}
        title={
          status === 'PENDING'
            ? `${t(
                tplStatus === ExperienceTemplateStatus.DRAFT ||
                tplStatus === ExperienceTemplateStatus.ROUGH_DRAFT
                  ? 'This spreadsheet will be created once you launch your experience'
                  : `This spreadsheet will be created once you save your changes`
              )}`
            : ''
        }
      >
        <img
          alt=""
          height={18}
          src={
            status === 'PENDING'
              ? '/images/resources/excel-logo.svg' // no disabled version atm
              : '/images/resources/excel-logo.svg'
          }
        />
      </Tooltip>
    </InputAdornment>
  );
};

const SpreadsheetAdornment = ({
  resource,
  tplStatus
}: {
  resource:
    | IExperienceTemplateResourceGoogleSheet
    | IExperienceTemplateResourceExcel;
  tplStatus: ExperienceTemplateStatus;
}) => {
  console.log('RES', resource);
  if (resource.type === TemplateResourceType.GOOGLE_SHEET) {
    return (
      <GoogleSheetAdorment status={resource.docStatus} tplStatus={tplStatus} />
    );
  }
  if (resource.type === TemplateResourceType.MS_EXCEL) {
    return (
      <ExcelSheetAdorment status={resource.status} tplStatus={tplStatus} />
    );
  }
  return null;
};

const SpreadsheetSettings = ({
  value,
  onChange,
  resources,
  onChangeResources,
  expTpl,
  customFields
}: {
  value: SubTaskDataDestinationSpreadsheet;
  onChange: (nextValue: SubTaskDataDestinationSpreadsheet) => void;
  expTpl: TaskBuilderContext;
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  customFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();
  const resource = getOptionalResource(resources, value);
  if (!resource) {
    console.warn('RESOURCE_NOT_FOUND', { storage: value, resources });
    return null;
  }

  // About column name validation:
  // - The column name can stay empty as long as the experience is a draft
  // - In this case it will be set to the question label upon launch
  // - This is to stabilize a column name after instantiating the spreadsheet
  // - Afterwards it's not allowed to ever have an empty column name

  return (
    <>
      <FormRow>
        <SpreadsheetResourcePicker
          resourceType={
            value.type === ExternalServiceType.GOOGLE_SHEET
              ? TemplateResourceType.GOOGLE_SHEET
              : TemplateResourceType.MS_EXCEL
          }
          resourceId={value.resourceId}
          onSelectResourceId={(resourceId) =>
            onChange({ ...value, resourceId })
          }
          resources={resources}
          onChangeResources={onChangeResources}
          expTpl={expTpl}
          customFields={customFields}
        />
      </FormRow>
      <FormRow>
        <TextField
          variant="outlined"
          label={`${t('subTask.question.storage.googleSheets.columnName')}`}
          value={value.columnName}
          onChange={(ev) => {
            onChange({ ...value, columnName: ev.target.value });
          }}
          fullWidth
        />
      </FormRow>
    </>
  );
};

/**
 * A fresh, empty (PENDING) spreadsheet resource. Single source of truth for the
 * empty-doc literal — used by the picker's "create new" dialog and its
 * auto-create-default behaviour, and available to other callers that need to
 * seed a resource.
 */
export const createEmptySpreadsheetResource = (
  resourceType:
    | TemplateResourceType.GOOGLE_SHEET
    | TemplateResourceType.MS_EXCEL,
  docTitle = ''
): IExperienceTemplateResourceGoogleSheet | IExperienceTemplateResourceExcel =>
  resourceType === TemplateResourceType.GOOGLE_SHEET
    ? {
        type: TemplateResourceType.GOOGLE_SHEET,
        resourceId: shortid(),
        docId: '',
        sheetId: '',
        docTitle,
        sheetTitle: '',
        docStatus: 'PENDING',
        sheetStatus: 'PENDING',
        mappings: DEFAULT_SHEET_MAPPINGS_V2,
        v: 2,
        additionalSaveParticipantToResourceMoments: []
      }
    : {
        type: TemplateResourceType.MS_EXCEL,
        resourceId: shortid(),
        docTitle,
        status: 'PENDING',
        driveId: '',
        driveItemId: '',
        sheetId: '',
        webUrl: '',
        mappings: DEFAULT_SHEET_MAPPINGS_V2,
        v: 2,
        additionalSaveParticipantToResourceMoments: []
      };

/**
 * The document (Google Sheet / Excel) resource picker: a create-or-select
 * dropdown with the status-aware sheet/excel adornment and per-option
 * "Open Document" + "Edit Field Mappings" actions.
 *
 * Extracted from {@link SpreadsheetSettings} so the SYNC_TO_DATA_DESTINATION
 * automation action reuses the exact same control (create + edit) as the
 * subtask data destinations — see
 * `AutomationBuilder/ActionSettings/SyncToDataDestination`. It operates purely
 * on a `resourceId` string so both the subtask storage shape and the
 * automation `destination` shape can drive it.
 *
 * `autoCreateDefault` mirrors the subtask `StorageConfiguration` behaviour: when
 * set and no resource is linked yet, the picker reuses the first existing
 * resource of this type or creates a fresh one — so the caller (the automation)
 * doesn't need its own find-or-create logic. Subtasks leave it off because they
 * always seed the resource before this renders.
 */
export const SpreadsheetResourcePicker = ({
  resourceType,
  resourceId,
  onSelectResourceId,
  resources,
  onChangeResources,
  expTpl,
  customFields,
  label,
  autoCreateDefault = false
}: {
  resourceType:
    | TemplateResourceType.GOOGLE_SHEET
    | TemplateResourceType.MS_EXCEL;
  resourceId: string;
  onSelectResourceId: (resourceId: string) => void;
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  label?: string;
  autoCreateDefault?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const [editedResource, setEditedResource] = useState<
    | IExperienceTemplateResourceGoogleSheet
    | IExperienceTemplateResourceExcel
    | null
  >(null);

  const defaultTitle = `Dado - ${expTpl.adminTitle || expTpl.title}`;

  const options = resources.filter(
    (r) => r.type === resourceType
  ) as (
    | IExperienceTemplateResourceGoogleSheet
    | IExperienceTemplateResourceExcel
  )[];
  const selected = options.find((r) => r.resourceId === resourceId) || null;

  const selectResource = (
    r: IExperienceTemplateResourceGoogleSheet | IExperienceTemplateResourceExcel
  ) => onSelectResourceId(r.resourceId);

  // Find-or-create a default resource when asked to (see `autoCreateDefault`).
  // Self-terminates once `resourceId` is set; re-runs when the type switches.
  useEffect(() => {
    if (!autoCreateDefault || resourceId) {
      return;
    }
    const existing = options[0];
    if (existing) {
      onSelectResourceId(existing.resourceId);
      return;
    }
    const created = createEmptySpreadsheetResource(resourceType);
    onChangeResources([...resources, created]);
    onSelectResourceId(created.resourceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCreateDefault, resourceId, resourceType]);

  return (
    <>
      <AutocompleteSingleWithCreateOption
        fullWidth
        label={label ?? `${t('subTask.question.storage.googleSheets.docTitle')}`}
        value={selected as any}
        onChange={(r) => r && selectResource(r)}
        options={options}
        getOptionLabel={(o) => o.docTitle || defaultTitle}
        getOptionAdornment={(o) => {
          return (
            <SpreadsheetAdornment resource={o} tplStatus={expTpl.status} />
          );
        }}
        renderOption={(o) => {
          // Render icon in gray or colored based on status
          return (
            <FlexContainer alignItems="center" spacing={0.5}>
              <SpreadsheetAdornment resource={o} tplStatus={expTpl.status} />
              <div>
                {o.docTitle || defaultTitle}
                <FlexContainer spacing={2}>
                  {o.type === TemplateResourceType.GOOGLE_SHEET &&
                    o.docStatus === 'COMPLETE' && (
                      <Button
                        target="_blank"
                        rel="noopener"
                        href={toGoogleSheetsLink(o.docId, o.sheetId)}
                        size="small"
                        onClick={(ev) => ev.stopPropagation()}
                        startIcon={<ExternalLink size={12} />}
                      >
                        {`${t('Open Document')}`}
                      </Button>
                    )}

                  {o.type === TemplateResourceType.MS_EXCEL &&
                    o.status === 'COMPLETE' && (
                      <Button
                        target="_blank"
                        rel="noopener"
                        href={o.webUrl}
                        size="small"
                        onClick={(ev) => ev.stopPropagation()}
                        startIcon={<ExternalLink size={12} />}
                      >
                        {`${t('Open Document')}`}
                      </Button>
                    )}
                  <Button
                    size="small"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      setEditedResource(o);
                    }}
                    startIcon={<Edit size={12} />}
                  >
                    {`${t('Edit Field Mappings')}`}
                  </Button>
                </FlexContainer>
              </div>
            </FlexContainer>
          );
        }}
        alwaysShowCreateOption
        renderCreateOption={(inputValue) =>
          inputValue ? `${t('Create')} "${inputValue}"` : t('Use a new sheet')
        }
        renderCreateDialog={({ inputValue, close }) => {
          const newResource = createEmptySpreadsheetResource(
            resourceType,
            inputValue
          );
          return (
            <EditSpreadSheetDialog
              open={true}
              onClose={close}
              value={newResource}
              onChange={(nextResource) => {
                onChangeResources([...resources, nextResource]);
                selectResource(nextResource);
              }}
              customFields={customFields}
              mode="create"
            />
          );
        }}
      />
      {editedResource && (
        <EditSpreadSheetDialog
          open={true}
          onClose={() => setEditedResource(null)}
          value={editedResource}
          onChange={(nextResource) => {
            const nextResources = resources.slice();
            const i = nextResources.findIndex(
              (r) => r.resourceId === nextResource.resourceId
            );
            if (i === -1) {
              // should never happen
              return;
            }
            nextResources[i] = nextResource;
            onChangeResources(nextResources);
          }}
          customFields={customFields}
        />
      )}
    </>
  );
};

const StorageConfiguration = ({
  value,
  onChange,
  resources,
  onChangeResources,
  expTpl,
  customFields,
  onBack,
  additionalInfo,
  isCompletionHookDataDestination,
  disallowUserProfile = false,
  tokens,
  storageContext,
  defualtCompletionText,
  sources,
  sourcesLabel
}: {
  value: SubTaskDataDestination;
  onChange: (nextValue: SubTaskDataDestination | null) => void;
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  onBack: () => void;
  additionalInfo?: React.ReactNode;
  disallowUserProfile: boolean;
  isCompletionHookDataDestination: boolean;
  tokens: TokenDefinition[];
  storageContext: StorageContext;
  defualtCompletionText?: string;
  sources?: string[];
  sourcesLabel?: string;
}) => {
  const { t } = useUnsafeTranslation();

  const [model, setModel] = useState<SubTaskDataDestination>(value);
  const _defualtCompletionText =
    defualtCompletionText || DEFAULT_COMPLETION_TEXT;

  // const validateBambooHrField = useFieldValidator(
  //   setValidation,
  //   VALIDATORS_QUESTION,
  //   'bambooHrField'
  // );

  // const invalidBambooHrFieldReason = validation?.fields.bambooHrField || null;

  // const validateGoogleSheetColumnName = useFieldValidator(
  //   setValidation,
  //   VALIDATORS_QUESTION,
  //   'googleSheetColumnName'
  // );
  // const invalidGoogleSheetColumnName =
  //   validation?.fields.googleSheetColumnName || null;

  // const validateUserProfileField = useFieldValidator(
  //   setValidation,
  //   VALIDATORS_QUESTION,
  //   'userProfileField'
  // );
  // const invalidUserProfileField = validation?.fields.userProfileField || null;

  const completionTextTokens: TokenDefinition[] = [
    ...tokens,
    {
      label: 'Completion Time',
      group: storageContext === StorageContext.SUBTASK ? 'Subtask' : 'Action',
      token: 'completionTime',
      color: 'green',
      type: 'date'
    }
  ];

  const selectedSource =
    sources && model.source && sources.includes(model.source)
      ? model.source
      : null;

  // Falls back to the raw source key when the translation is missing — i18next
  // returns the key path unchanged in that case, which would otherwise leak
  // the prefix into the UI.
  const translateSource = (o: string) => translateSourceLabel(t, o);

  return (
    <>
      {sources && (
        <FormRow>
          <AutocompleteSingle
            label={sourcesLabel}
            fullWidth
            value={selectedSource}
            options={sources}
            getOptionLabel={translateSource}
            getOptionSelected={(o, v) => o === v}
            onChange={(next) => {
              setModel({ ...model, source: next ?? undefined });
            }}
            renderOption={translateSource}
          />
        </FormRow>
      )}
      <FormRow>
        <StorageTypeSelector
          value={model.type}
          disallowUserProfile={disallowUserProfile}
          onChange={(nextType) => {
            if (nextType === InternalStorageInstructionType.USER_PROFILE) {
              const newModel: SubTaskDataDestinationUserProfile = isCompletionHookDataDestination
                ? {
                    type: InternalStorageInstructionType.USER_PROFILE,
                    destinationType: DestinatioType.COMPLETION_HOOK,
                    completionText: _defualtCompletionText,
                    field: '',
                    ...(model.source ? { source: model.source } : {})
                  }
                : {
                    type: InternalStorageInstructionType.USER_PROFILE,
                    destinationType: DestinatioType.SUBTASK_ANSWER,
                    field: '',
                    ...(model.source ? { source: model.source } : {})
                  };
              setModel(newModel);
              return;
            }
            if (nextType === ExternalServiceType.ADP_WORKFORCE_NOW) {
              const newModel: SubTaskDataDestinationAdpWorkforceNow = isCompletionHookDataDestination
                ? {
                    type: ExternalServiceType.ADP_WORKFORCE_NOW,
                    field: null,
                    destinationType: DestinatioType.COMPLETION_HOOK,
                    completionText: DEFAULT_COMPLETION_TEXT,
                    ...(model.source ? { source: model.source } : {})
                  }
                : {
                    type: ExternalServiceType.ADP_WORKFORCE_NOW,
                    field: null,
                    destinationType: DestinatioType.SUBTASK_ANSWER,
                    ...(model.source ? { source: model.source } : {})
                  };
              setModel(newModel);
              return;
            }
            if (nextType === ExternalServiceType.BAMBOO_HR) {
              const newModel: SubTaskDataDestinationBambooHr = isCompletionHookDataDestination
                ? {
                    type: ExternalServiceType.BAMBOO_HR,
                    destinationType: DestinatioType.COMPLETION_HOOK,
                    completionText: _defualtCompletionText,
                    field: null,
                    ...(model.source ? { source: model.source } : {})
                  }
                : {
                    type: ExternalServiceType.BAMBOO_HR,
                    destinationType: DestinatioType.SUBTASK_ANSWER,
                    field: null,
                    ...(model.source ? { source: model.source } : {})
                  };
              setModel(newModel);

              return;
            }
            if (nextType === ExternalServiceType.GOOGLE_SHEET) {
              let resource = resources.find(
                (r) => r.type === TemplateResourceType.GOOGLE_SHEET
              );
              if (!resource) {
                resource = {
                  type: TemplateResourceType.GOOGLE_SHEET,
                  resourceId: shortid(),
                  docId: '',
                  sheetId: '',
                  docTitle: '',
                  sheetTitle: '',
                  docStatus: 'PENDING',
                  sheetStatus: 'PENDING',
                  mappings: DEFAULT_SHEET_MAPPINGS_V2,
                  v: 2,
                  additionalSaveParticipantToResourceMoments: []
                };
                onChangeResources([...resources, resource]);
              }

              const storage: SubTaskDataDestinationGoogleSheet = isCompletionHookDataDestination
                ? {
                    type: ExternalServiceType.GOOGLE_SHEET,
                    resourceId: resource.resourceId,
                    columnName: '',
                    destinationType: DestinatioType.COMPLETION_HOOK,
                    completionText: _defualtCompletionText,
                    ...(model.source ? { source: model.source } : {})
                  }
                : {
                    type: ExternalServiceType.GOOGLE_SHEET,
                    resourceId: resource.resourceId,
                    columnName: '',
                    destinationType: DestinatioType.SUBTASK_ANSWER,
                    ...(model.source ? { source: model.source } : {})
                  };
              setModel(storage);
              return;
            }

            if (nextType === ExternalServiceType.MS_EXCEL) {
              let resource = resources.find(
                (r) => r.type === TemplateResourceType.MS_EXCEL
              );
              if (!resource) {
                const excelresource: IExperienceTemplateResourceExcel = {
                  type: TemplateResourceType.MS_EXCEL,
                  resourceId: shortid(),
                  driveId: '',
                  driveItemId: '',
                  sheetId: '',
                  webUrl: '',
                  docTitle: '',
                  status: 'PENDING',
                  mappings: DEFAULT_SHEET_MAPPINGS_V2,
                  v: 2,
                  additionalSaveParticipantToResourceMoments: []
                };
                resource = excelresource;
                onChangeResources([...resources, resource]);
              }

              const storage: SubTaskDataDestinationExcel = isCompletionHookDataDestination
                ? {
                    type: ExternalServiceType.MS_EXCEL,
                    resourceId: resource.resourceId,
                    columnName: '',
                    destinationType: DestinatioType.COMPLETION_HOOK,
                    completionText: _defualtCompletionText,
                    ...(model.source ? { source: model.source } : {})
                  }
                : {
                    type: ExternalServiceType.MS_EXCEL,
                    resourceId: resource.resourceId,
                    columnName: '',
                    destinationType: DestinatioType.SUBTASK_ANSWER,
                    ...(model.source ? { source: model.source } : {})
                  };
              setModel(storage);
              return;
            }

            if (nextType === InternalStorageInstructionType.SUPPORTING_ACTOR) {
              // this is not assign from within this componen so this should never happen anyways.
              return;
            }

            if (nextType === ExternalServiceType.PERSONIO) {
              const newModel: SubTaskDataDestinationPersonio = isCompletionHookDataDestination
                ? {
                    type: ExternalServiceType.PERSONIO,
                    field: null,
                    destinationType: DestinatioType.COMPLETION_HOOK,
                    completionText: DEFAULT_COMPLETION_TEXT,
                    ...(model.source ? { source: model.source } : {})
                  }
                : {
                    type: ExternalServiceType.PERSONIO,
                    field: null,
                    destinationType: DestinatioType.SUBTASK_ANSWER,
                    ...(model.source ? { source: model.source } : {})
                  };

              setModel(newModel);
              return;
            }

            if (nextType === ExternalServiceType.SALESFORCE) {
              // Find-or-create an SF template resource so the subtask
              // destination always has a valid resourceId to reference.
              // Multi-resource templates can add more via the picker inside
              // SalesforceSettings. Default-create a Contact resource if none
              // exist — keeps single-resource templates one click away from
              // working state.
              let resource = resources.find(
                (r): r is IExperienceTemplateResourceSalesforce =>
                  r.type === TemplateResourceType.SALESFORCE
              );
              if (!resource) {
                resource = {
                  type: TemplateResourceType.SALESFORCE,
                  resourceId: shortid(),
                  status: 'PENDING',
                  objectName: 'Contact',
                  externalIdField: '',
                  fieldMap: {}
                };
                onChangeResources([...resources, resource]);
              }

              const newModel: SubTaskDataDestinationSalesforce = isCompletionHookDataDestination
                ? {
                    type: ExternalServiceType.SALESFORCE,
                    resourceId: resource.resourceId,
                    objectName: 'Contact',
                    fieldName: null,
                    destinationType: DestinatioType.COMPLETION_HOOK,
                    completionText: DEFAULT_COMPLETION_TEXT
                  }
                : {
                    type: ExternalServiceType.SALESFORCE,
                    resourceId: resource.resourceId,
                    objectName: 'Contact',
                    fieldName: null,
                    destinationType: DestinatioType.SUBTASK_ANSWER
                  };
              setModel(newModel);
              return;
            }

            assertNever(nextType);
          }}
        />
      </FormRow>
      {model.type === ExternalServiceType.ADP_WORKFORCE_NOW && (
        <AdpWorkforceNowSettings
          expTpl={expTpl}
          value={model}
          onChange={setModel}
        />
      )}
      {model.type === ExternalServiceType.PERSONIO && (
        <PersonioSettings expTpl={expTpl} value={model} onChange={setModel} />
      )}
      {model.type === ExternalServiceType.SALESFORCE && (
        <SalesforceSettings
          expTpl={expTpl}
          value={model}
          onChange={setModel}
          resources={resources}
          onChangeResources={onChangeResources}
        />
      )}
      {model.type === ExternalServiceType.BAMBOO_HR && (
        <BambooHrSettings expTpl={expTpl} value={model} onChange={setModel} />
      )}
      {model.type === ExternalServiceType.GOOGLE_SHEET && (
        <SpreadsheetSettings
          key="google"
          expTpl={expTpl}
          value={model}
          onChange={setModel}
          resources={resources}
          onChangeResources={onChangeResources}
          customFields={customFields}
        />
      )}

      {model.type === ExternalServiceType.MS_EXCEL && (
        <SpreadsheetSettings
          key="excel"
          expTpl={expTpl}
          value={model}
          onChange={setModel}
          resources={resources}
          onChangeResources={onChangeResources}
          customFields={customFields}
        />
      )}
      {model.type === InternalStorageInstructionType.USER_PROFILE && (
        <UserProfileSettings
          expTpl={expTpl}
          value={model}
          onChange={setModel}
          customFields={customFields}
        />
      )}

      {model.destinationType === DestinatioType.COMPLETION_HOOK && (
        <FormRow>
          <TextFieldWithReplacementTokens
            fullWidth
            variant="outlined"
            label={
              model.type === ExternalServiceType.MS_EXCEL ||
              model.type === ExternalServiceType.GOOGLE_SHEET
                ? t('Written to the column')
                : t('Written to the field')
            }
            value={model.completionText}
            onChange={(newText) => {
              const nextvalue = { ...model };
              if (
                nextvalue.destinationType === DestinatioType.COMPLETION_HOOK
              ) {
                nextvalue.completionText = newText;
              }

              setModel(nextvalue);
            }}
            tokens={completionTextTokens}
          />
        </FormRow>
      )}
      {additionalInfo && <FormRow>{additionalInfo}</FormRow>}
      <FormRow>
        <FlexContainer justifyContent="space-between" fullWidth>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              onChange(null);
              onBack();
            }}
          >
            {t('Remove')}
          </Button>
          <FlexContainer justifyContent="flex-end" fullWidth>
            <Button variant="outlined" onClick={onBack}>
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                onChange(model);
                onBack();
              }}
            >
              {t('Save')}
            </Button>
          </FlexContainer>
        </FlexContainer>
      </FormRow>
    </>
  );
};

const getStorageDestinationHeaderData = (
  type: SubTaskDataDestination['type']
): {
  title: string;
  iconSrc: string;
} => {
  switch (type) {
    case ExternalServiceType.ADP_WORKFORCE_NOW:
      return {
        title: 'ADP Workforce Now',
        iconSrc: '/images/integrations-logos/adp.svg'
      };
    case ExternalServiceType.PERSONIO:
      return {
        title: 'Personio',
        iconSrc: '/images/integrations-logos/personio.svg'
      };
    case ExternalServiceType.BAMBOO_HR:
      return {
        title: 'BambooHR',
        iconSrc: '/images/resources/bamboohr-logo.png'
      };
    case ExternalServiceType.GOOGLE_SHEET:
      return {
        title: 'Google Sheets',
        iconSrc: '/images/resources/gsheet.svg'
      };
    case ExternalServiceType.MS_EXCEL:
      return {
        title: 'Microsoft Excel',
        iconSrc: '/images/resources/excel-logo.svg'
      };
    case ExternalServiceType.SALESFORCE:
      return {
        title: 'Salesforce',
        iconSrc: '/images/integrations-logos/salesforce.svg'
      };
    case InternalStorageInstructionType.USER_PROFILE:
      return {
        title: 'Dado',
        iconSrc: '/images/logo.svg'
      };
    case InternalStorageInstructionType.SUPPORTING_ACTOR:
      // Should not hapen, not sure if log or throw
      return { title: '', iconSrc: '' };
    default:
      return assertNever(type);
  }
};

const StorageDestinationContainer = styled('div')<{ error?: boolean }>((p) => ({
  cursor: p.onClick ? 'pointer' : undefined,
  padding: `${p.theme.spacing(2)}px 0`,
  borderBottom: p.theme.custom.border.standard,
  display: 'grid',
  width: '100%',
  gridTemplateColumns: '1fr min-content',
  color: p.error ? p.theme.palette.error.main : undefined
}));

const StorageDetailsErrorContainer = styled('div')((p) => ({
  color: p.theme.palette.error.main
}));

const StorageDetailsErrors = ({
  errors
}: {
  errors: { [key: string]: string };
}) => {
  return (
    <>
      {Object.values(errors).map((e, i) => (
        <StorageDetailsErrorContainer key={i}>{e}</StorageDetailsErrorContainer>
      ))}
    </>
  );
};

const StorageDetailsUserProfile = ({
  storage,
  customFields
}: {
  storage: SubTaskDataDestinationUserProfile;
  customFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);
  const field = userFields.find((f) => f.key === storage.field);
  return (
    <div>
      {field?.label
        ? `${t('Data Field')} "${field.label}"`
        : t('Please select the field to save answers to')}
    </div>
  );
};

const StorageDetailSpreadsheet = ({
  storage,
  resources,
  expTpl
}: {
  storage: SubTaskDataDestinationSpreadsheet;
  resources: IExperienceTemplateResource[];
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  const resource = getOptionalResource(resources, storage);
  if (!resource) {
    console.warn('RESOURCE_NOT_FOUND', { storage, resources });
    return null;
  }
  return (
    <>
      <div>
        {resource.docTitle || `Dado - ${expTpl.adminTitle || expTpl.title}`}
      </div>
      <div>
        {storage.columnName
          ? `${t('Column')} "${storage.columnName}"`
          : t('Please define a column name')}
      </div>
    </>
  );
};

const StorageDetailsBambooHr = ({
  storage
}: {
  storage: SubTaskDataDestinationBambooHr;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <div>
      {storage.field?.name
        ? `${t('Field')} "${storage.field.name}"`
        : t('Please select the field to save answers to')}
    </div>
  );
};

const StorageDetailsPersonio = ({
  orgId,
  storage
}: {
  orgId: string;
  storage: SubTaskDataDestinationPersonio;
}) => {
  const { t } = useUnsafeTranslation();
  const [options] = usePersonioDestinationFields(orgId);
  const label =
    options?.find((o) => o.key === storage.field)?.label || t('Loading...');
  return (
    <div>
      {storage.field
        ? `${t('Field')} "${label}"`
        : t('Please select the field to save answers to')}
    </div>
  );
};

const StorageDetailsAdpWorkforceNow = ({
  orgId,
  storage
}: {
  orgId: string;
  storage: SubTaskDataDestinationAdpWorkforceNow;
}) => {
  const { t } = useUnsafeTranslation();
  const [options] = useAdpWorkforceNowDataDestinationFields(orgId);
  const label =
    options?.find((o) => o.key === storage.field)?.label || t('Loading...');
  return (
    <div>
      {storage.field
        ? `${t('Field')} "${label}"`
        : t('Please select the field to save answers to')}
    </div>
  );
};

const StorageDetails = ({
  storage,
  resources,
  customFields,
  expTpl,
  errors
}: {
  storage: SubTaskDataDestination;
  resources: IExperienceTemplateResource[];
  customFields: CustomUserField[];
  expTpl: TaskBuilderContext;
  errors?: { [key: string]: string } | null;
}) => {
  if (errors) {
    return <StorageDetailsErrors errors={errors} />;
  }
  if (storage.type === InternalStorageInstructionType.USER_PROFILE) {
    return (
      <StorageDetailsUserProfile
        storage={storage}
        customFields={customFields}
      />
    );
  }
  if (storage.type === ExternalServiceType.BAMBOO_HR) {
    return <StorageDetailsBambooHr storage={storage} />;
  }
  if (storage.type === ExternalServiceType.ADP_WORKFORCE_NOW) {
    return (
      <StorageDetailsAdpWorkforceNow orgId={expTpl.orgId} storage={storage} />
    );
  }
  if (storage.type === ExternalServiceType.PERSONIO) {
    return <StorageDetailsPersonio orgId={expTpl.orgId} storage={storage} />;
  }
  if (
    storage.type === ExternalServiceType.GOOGLE_SHEET ||
    storage.type === ExternalServiceType.MS_EXCEL
  ) {
    return (
      <StorageDetailSpreadsheet
        storage={storage}
        resources={resources}
        expTpl={expTpl}
      />
    );
  }
  return null;
};

const StorageDetailsContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(0.5),
  lineHeight: '1.5rem'
}));

const StorageDestination = ({
  expTpl,
  storage,
  goToSubPage,
  customFields,
  resources,
  errors,
  sources
}: {
  expTpl: TaskBuilderContext;
  storage: SubTaskDataDestination;
  goToSubPage?: () => void;
  resources: IExperienceTemplateResource[];
  customFields: CustomUserField[];
  errors?: { [key: string]: string };
  sources?: string[];
}) => {
  const { t } = useUnsafeTranslation();
  const { title, iconSrc } = getStorageDestinationHeaderData(storage.type);
  const sourceLabel =
    sources && storage.source && sources.includes(storage.source)
      ? translateSourceLabel(t, storage.source)
      : undefined;
  return (
    <StorageDestinationContainer
      onClick={goToSubPage}
      role={goToSubPage ? 'button' : undefined}
      error={!!errors}
    >
      <div>
        <FlexContainer>
          <img height={16} src={iconSrc} alt="" />
          <div>{title}</div>
        </FlexContainer>
        {sourceLabel && (
          <Typography variant="caption">
            <StorageDetailsContainer>{sourceLabel}</StorageDetailsContainer>
          </Typography>
        )}
        <Typography variant="caption">
          <StorageDetailsContainer>
            <StorageDetails
              storage={storage}
              resources={resources}
              customFields={customFields}
              expTpl={expTpl}
              errors={errors}
            />
          </StorageDetailsContainer>
        </Typography>
      </div>
      {goToSubPage && (
        <ChevronRight size={20} style={{ alignSelf: 'center' }} />
      )}
    </StorageDestinationContainer>
  );
};

const StorageDestinationsActions = styled('div')((p) => ({
  marginTop: p.theme.spacing(2)
}));

export const StorageDestinations = ({
  value,
  onChange,
  resources,
  onChangeResources,
  expTpl,
  customFields,
  goToSubPage,
  validation,
  additionalEditInfo,
  isCompletionHookDataDestination,
  hookIndex = 0,
  disallowUserProfile = false,
  storageContext,
  tokens,
  defualtCompletionText,
  sources,
  sourcesLabel
}: {
  // value: ISubTaskQuestionTemplate;
  value: SubTaskDataDestination[];
  resources: IExperienceTemplateResource[];
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  onChange?: (nextValue: SubTaskDataDestination[]) => void;
  onChangeResources?: (nextResources: IExperienceTemplateResource[]) => void;
  goToSubPage?: (nextSubPage: BlockSettingsSubPage) => void;
  validation?: TaskBuilderBlockValidationStateItem | null;
  additionalEditInfo?: (props: {
    v: SubTaskDataDestination;
  }) => React.ReactNode;
  disallowUserProfile: boolean;
  isCompletionHookDataDestination: boolean;
  tokens: TokenDefinition[];
  storageContext: StorageContext;
  hookIndex?: number;
  defualtCompletionText?: string;
  /** Raw source keys for the picker, persisted as-is on `storage.source`.
   * Omitted by subtasks that emit a single implicit value (e.g. QUESTION) —
   * picker is hidden in that case. Display labels are resolved via
   * `SOURCE_TRANSLATION_PREFIX`. */
  sources?: string[];
  /** Label for the source picker. Only meaningful when `sources` is set. */
  sourcesLabel?: string;
}) => {
  const _defualtCompletionText =
    defualtCompletionText || DEFAULT_COMPLETION_TEXT;
  const { t } = useUnsafeTranslation();
  // Gated by the per-user NEW_UX flag — classic users' output is unchanged.
  const newUx = useNewUx().enabled;
  const storageErrors: {
    [key: string]: string;
  }[] = isCompletionHookDataDestination
    ? (JSON.parse(validation?.fields?.hooks || '[[]]') as any[][])[hookIndex]
    : JSON.parse(validation?.fields?.storage || '[]');
  const { active: activeIntegrations } = useIntegrationsContext();
  const storageTypes = useMemo<StorageType[]>(() => {
    // I got this from the StorageTypeSelector maybe  we could maybe past this value down there and reuse this
    return compact([
      activeIntegrations.has(Integration.ADP_WORKFORCE_NOW) && {
        label: 'ADP Workforce Now',
        type: ExternalServiceType.ADP_WORKFORCE_NOW
      },
      activeIntegrations.has(Integration.PERSONIO) && {
        label: 'Personio',
        type: ExternalServiceType.PERSONIO
      },
      activeIntegrations.has(Integration.GOOGLE_SHEETS) && {
        label: 'Google Sheet',
        type: ExternalServiceType.GOOGLE_SHEET
      },
      activeIntegrations.has(Integration.MICROSOFT) && {
        label: 'Microsoft Excel',
        type: ExternalServiceType.MS_EXCEL
      },
      activeIntegrations.has(Integration.BAMBOOHR) && {
        label: 'BambooHR',
        type: ExternalServiceType.BAMBOO_HR
      },
      !disallowUserProfile && {
        label: t('Dado'),
        type: InternalStorageInstructionType.USER_PROFILE
      }
    ]);
  }, [activeIntegrations, t, disallowUserProfile]);

  const makeGoToSubPage = (storage: SubTaskDataDestination, i: number) => {
    if (!onChange || !onChangeResources || !goToSubPage) {
      return undefined;
    }
    return () => {
      const p: BlockSettingsSubPage = {
        header: {
          def: {
            icon: Database,
            label: t('Edit Data Destination')
          }
        },
        body: ({ onBack, resources: res, onChangeResources: onChangeRes }) => (
          <StorageConfiguration
            isCompletionHookDataDestination={isCompletionHookDataDestination}
            disallowUserProfile={disallowUserProfile}
            value={storage}
            onChange={(nextStorage) => {
              const nextStorages: (SubTaskDataDestination | null)[] = value.slice();
              nextStorages[i] = nextStorage;
              onChange(compact(nextStorages));
            }}
            resources={res}
            onChangeResources={onChangeRes}
            expTpl={expTpl}
            customFields={customFields}
            onBack={onBack}
            additionalInfo={additionalEditInfo?.({ v: storage }) || undefined}
            tokens={tokens}
            storageContext={storageContext}
            defualtCompletionText={_defualtCompletionText}
            sources={sources}
            sourcesLabel={sourcesLabel}
          />
        )
      };
      goToSubPage(p);
    };
  };

  const getSubTaskDataDestinationInitialValue = (
    type: SubTaskDataDestination['type'],
    isCompletionHookDataDestination: boolean,
    _defualtCompletionText: string
  ): SubTaskDataDestination => {
    switch (type) {
      case InternalStorageInstructionType.USER_PROFILE:
        const storageDestination: SubTaskDataDestinationUserProfile = isCompletionHookDataDestination
          ? {
              type: InternalStorageInstructionType.USER_PROFILE,
              field: '',
              destinationType: DestinatioType.COMPLETION_HOOK,
              completionText: _defualtCompletionText
            }
          : {
              type: InternalStorageInstructionType.USER_PROFILE,
              field: '',
              destinationType: DestinatioType.SUBTASK_ANSWER
            };
        return storageDestination;
      case ExternalServiceType.GOOGLE_SHEET:
        // this is slightly modified copy paste code maybe this should be extracted to a function
        let resource = resources.find(
          (r) => r.type === TemplateResourceType.GOOGLE_SHEET
        );
        if (!resource) {
          resource = {
            type: TemplateResourceType.GOOGLE_SHEET,
            resourceId: shortid(),
            docId: '',
            sheetId: '',
            docTitle: '',
            sheetTitle: '',
            docStatus: 'PENDING',
            sheetStatus: 'PENDING',
            mappings: DEFAULT_SHEET_MAPPINGS_V2,
            v: 2,
            additionalSaveParticipantToResourceMoments: []
          };
          onChangeResources && onChangeResources([...resources, resource]);
        }

        const storage: SubTaskDataDestinationGoogleSheet = isCompletionHookDataDestination
          ? {
              type: ExternalServiceType.GOOGLE_SHEET,
              resourceId: resource.resourceId,
              columnName: '',
              destinationType: DestinatioType.COMPLETION_HOOK,
              completionText: _defualtCompletionText
            }
          : {
              type: ExternalServiceType.GOOGLE_SHEET,
              resourceId: resource.resourceId,
              columnName: '',
              destinationType: DestinatioType.SUBTASK_ANSWER
            };
        return storage;
      case ExternalServiceType.MS_EXCEL:
        let excelResource = resources.find(
          (r) => r.type === TemplateResourceType.MS_EXCEL
        );
        if (!excelResource) {
          excelResource = {
            type: TemplateResourceType.MS_EXCEL,
            resourceId: shortid(),
            docTitle: '',
            status: 'PENDING',
            driveId: '',
            driveItemId: '',
            sheetId: '',
            webUrl: '',
            mappings: DEFAULT_SHEET_MAPPINGS_V2,
            v: 2,
            additionalSaveParticipantToResourceMoments: []
          };
          onChangeResources && onChangeResources([...resources, excelResource]);
        }

        const excelStorage: SubTaskDataDestinationExcel = isCompletionHookDataDestination
          ? {
              type: ExternalServiceType.MS_EXCEL,
              resourceId: excelResource.resourceId,
              columnName: '',
              destinationType: DestinatioType.COMPLETION_HOOK,
              completionText: _defualtCompletionText
            }
          : {
              type: ExternalServiceType.MS_EXCEL,
              resourceId: excelResource.resourceId,
              columnName: '',
              destinationType: DestinatioType.SUBTASK_ANSWER
            };
        return excelStorage;
      case ExternalServiceType.BAMBOO_HR:
        const bambooStorageDestination: SubTaskDataDestinationBambooHr = isCompletionHookDataDestination
          ? {
              type: ExternalServiceType.BAMBOO_HR,
              field: null,
              destinationType: DestinatioType.COMPLETION_HOOK,
              completionText: _defualtCompletionText
            }
          : {
              type: ExternalServiceType.BAMBOO_HR,
              field: null,
              destinationType: DestinatioType.SUBTASK_ANSWER
            };
        return bambooStorageDestination;
      case ExternalServiceType.ADP_WORKFORCE_NOW:
        const adpWorkforceNowStorageDestination: SubTaskDataDestinationAdpWorkforceNow = isCompletionHookDataDestination
          ? {
              type: ExternalServiceType.ADP_WORKFORCE_NOW,
              field: null,
              destinationType: DestinatioType.COMPLETION_HOOK,
              completionText: DEFAULT_COMPLETION_TEXT
            }
          : {
              type: ExternalServiceType.ADP_WORKFORCE_NOW,
              field: null,
              destinationType: DestinatioType.SUBTASK_ANSWER
            };
        return adpWorkforceNowStorageDestination;
      case ExternalServiceType.PERSONIO:
        const personioStorageDestination: SubTaskDataDestinationPersonio = isCompletionHookDataDestination
          ? {
              type: ExternalServiceType.PERSONIO,
              field: null,
              destinationType: DestinatioType.COMPLETION_HOOK,
              completionText: DEFAULT_COMPLETION_TEXT
            }
          : {
              type: ExternalServiceType.PERSONIO,
              field: null,
              destinationType: DestinatioType.SUBTASK_ANSWER
            };
        return personioStorageDestination;
      case InternalStorageInstructionType.SUPPORTING_ACTOR:
        const supportingActorDataDestination: SubTaskDataDestinationSupportingActor = isCompletionHookDataDestination
          ? {
              type: InternalStorageInstructionType.SUPPORTING_ACTOR,
              supportingActorToAssign: '',
              destinationType: DestinatioType.COMPLETION_HOOK,
              completionText: _defualtCompletionText
            }
          : {
              type: InternalStorageInstructionType.SUPPORTING_ACTOR,
              supportingActorToAssign: '',
              destinationType: DestinatioType.SUBTASK_ANSWER
            };

        return supportingActorDataDestination;
    }
  };
  return (
    <>
      {/* Beware that makeGoToSubPage relies on the indexes to correctly identify the
      Storage Destinations so filtering or sorting will break that and cause it to write to the wrng index*/}
      {value.map((s, i) =>
        s.type !== InternalStorageInstructionType.SUPPORTING_ACTOR ? (
          <StorageDestination
            key={i}
            storage={s}
            goToSubPage={makeGoToSubPage(s, i)}
            customFields={customFields}
            resources={resources}
            expTpl={expTpl}
            errors={storageErrors[i]}
            sources={sources}
          />
        ) : null
      )}
      {onChange && (
        <StorageDestinationsActions>
          {storageTypes.length > 0 && (
            <Button
              size="small"
              color="primary"
              startIcon={<PlusCircle size={20} />}
              style={newUx ? { color: '#1D4ED8' } : undefined}
              onClick={() => {
                const type: SubTaskDataDestination['type'] = !disallowUserProfile
                  ? InternalStorageInstructionType.USER_PROFILE
                  : storageTypes[0].type;
                const newDataDestination = getSubTaskDataDestinationInitialValue(
                  type,
                  isCompletionHookDataDestination,
                  _defualtCompletionText
                );
                const storagesWithNewDest: SubTaskDataDestination[] = [
                  ...value,
                  newDataDestination
                ];
                onChange(storagesWithNewDest);
                const lastI = storagesWithNewDest.length - 1;
                makeGoToSubPage(storagesWithNewDest[lastI], lastI)?.();
              }}
            >
              {t('Add Destination')}
            </Button>
          )}
        </StorageDestinationsActions>
      )}
    </>
  );
};
